import { DateService } from '@/coreDomainsModule/services/dateService';
import { BaseTableContentViewModel } from '../../baseTableContentViewModel';
import { TrashBinHeaderKey } from '../../interface/tableContentHeaders';
import { TableContentItem } from '../../interface/tableContentItems';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IYearMonth } from '@/coreDomainsModule/date';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';

/**
 * ゴミ箱のテーブル表示に使うロジック
 */
export class TrashBinTableContentViewModel extends BaseTableContentViewModel {
  /**
   * テーブルの内容表示に使う処理
   * @param headerKey - ヘッダー情報
   * @param item - 表示するオブジェクト
   */
  public displayTrashBinItem = (headerKey: TrashBinHeaderKey, item: TableContentItem): string => {
    const business = item as IBusiness;
    const targetDate = new Date();
    return this._distinctionNoneData(
      (() => {
        switch (headerKey) {
          case 'TrashBinWarehouseNumber':
            return business.warehouseNumber;
          case 'TrashBinWarehouse':
            return business?.warehouse;
          case 'TrashBinSupplierNumber':
            return business.supplierNumber;
          case 'TrashBinSupplierName':
            return business?.supplierName;
          case 'TrashBinBusinessNumber':
            return business?.businessNumber;
          case 'TrashBinBusinessName':
            return business?.businessName;
          case 'TrashBinPackage':
            return business?.package;
          case 'TrashBinUnit':
            return business.unit;
          case 'TrashBinCustomerNumber':
            return business.customerNumber;
          case 'TrashBinCustomerName':
            return business?.customerName;
          case 'TrashBinDeliveryDestinationNumber':
            return business.salesDestinationNumber;
          case 'TrashBinDeliveryDestinationName':
            return business?.salesDestinationName;
          case 'TrashBinManufacturer':
            return business?.manufacturer;
          case 'TrashBinEndUser':
            return business?.endUser;
          case 'TrashBinCustomerForecastResponsibleUser':
            if (!business?.baseBusinessResponsibleUsers || business.baseBusinessResponsibleUsers.length === 0) return '--';
            return business.baseBusinessResponsibleUsers.map((user) => UserAccountService.getDisplayName(user)).join(', ');
          case 'TrashBinSupplierForecastResponsibleUser':
            if (!business?.aggregateBusinessResponsibleUsers || business.aggregateBusinessResponsibleUsers.length === 0) return '--';
            return business.aggregateBusinessResponsibleUsers.map((user) => UserAccountService.getDisplayName(user)).join(', ');
          case 'TrashBinDaysRemainUntilDeletion':
            return Math.ceil(((business?.updatedAt ?? 0) - Date.now()) / (1000 * 60 * 60 * 24) + 45).toString();
          case 'TrashBinArrival':
            if (!business?.arrivals || !business.arrivals.length) return '--';
            // 当月-1の入荷を取得
            const targetArrivals = business.arrivals.filter((arrival) =>
              DateService.equalYearMonthInfo(
                arrival,
                DateService.convertDateToIYearMonth(DateService.getShiftedDateByMonths(targetDate, -1))
              )
            );
            const arrivalAmountValue = !!targetArrivals.length ? Number(targetArrivals[0].amount) : NaN;
            return !isNaN(arrivalAmountValue) ? arrivalAmountValue.addComma() : '--';
          case 'TrashBinSalesResults':
            if (!business?.salesResults || !business?.salesResults.length) return '--';
            // 当月-1の販売実績を取得
            const targetSalesResults = business.salesResults.filter((salesResult) =>
              DateService.equalYearMonthInfo(
                salesResult,
                DateService.convertDateToIYearMonth(DateService.getShiftedDateByMonths(targetDate, -1))
              )
            );
            const salesResultAmountValue = !!targetSalesResults.length ? Number(targetSalesResults[0].amount) : NaN;
            return !isNaN(salesResultAmountValue) ? salesResultAmountValue.addComma() : '--';
          case 'TrashBinLatestInventoryAmount':
            if (business?.inventory == null) return '--';
            const inventoryAmountValue = Number(business?.inventory);
            return !isNaN(inventoryAmountValue) ? inventoryAmountValue.addComma() : '--';
          case 'TrashBinExecuteDate':
            return this._convertDateToString(business.executionDate);
          case 'TrashBinCustomerAnnounce-1':
            // 当月の内示を取得
            return (
              business.customerAnnounce
                ?.filter((announce) => DateService.equalYearMonthInfo(announce, DateService.convertDateToIYearMonth(targetDate)))
                .reduce((acc, cur) => acc + cur.amount, 0)
                .addComma() ?? '--'
            );
          case 'TrashBinCustomerAnnounce-2':
            // 当月+1の内示を取得
            return (
              business.customerAnnounce
                ?.filter((announce) =>
                  DateService.equalYearMonthInfo(
                    announce,
                    DateService.convertDateToIYearMonth(DateService.getShiftedDateByMonths(targetDate, 1))
                  )
                )
                .reduce((acc, cur) => acc + cur.amount, 0)
                .addComma() ?? '--'
            );
          case 'TrashBinCustomerAnnounce-3':
            // 当月+2の内示を取得
            return (
              business.customerAnnounce
                ?.filter((announce) =>
                  DateService.equalYearMonthInfo(
                    announce,
                    DateService.convertDateToIYearMonth(DateService.getShiftedDateByMonths(targetDate, 2))
                  )
                )
                .reduce((acc, cur) => acc + cur.amount, 0)
                .addComma() ?? '--'
            );
        }
      })()
    );
  };

  /**
   * Headerのキーを加工して返す処理
   * @param headerKey - 取得するHeaderのキー
   * @param forecastStartYearMonth - 情報の基準月
   */
  public getHeaderText = (headerKey: string | undefined, forecastStartYearMonth: IYearMonth | undefined) => {
    if (!!!headerKey) return;
    const trashBinHeaderKey = headerKey as TrashBinHeaderKey;
    switch (trashBinHeaderKey) {
      case 'TrashBinArrival':
        return forecastStartYearMonth ? forecastStartYearMonth.month + '月入荷' : trashBinHeaderKey;

      case 'TrashBinSalesResults':
        return forecastStartYearMonth ? forecastStartYearMonth.month + '月実績' : trashBinHeaderKey;

      case 'TrashBinCustomerAnnounce-1':
        return forecastStartYearMonth
          ? DateService.updateYearMonth(forecastStartYearMonth.year, forecastStartYearMonth.month, 1).updateMonth + '月内示'
          : trashBinHeaderKey;

      case 'TrashBinCustomerAnnounce-2':
        return forecastStartYearMonth
          ? DateService.updateYearMonth(forecastStartYearMonth.year, forecastStartYearMonth.month, 2).updateMonth + '月内示'
          : trashBinHeaderKey;

      case 'TrashBinCustomerAnnounce-3':
        return forecastStartYearMonth
          ? DateService.updateYearMonth(forecastStartYearMonth.year, forecastStartYearMonth.month, 3).updateMonth + '月内示'
          : trashBinHeaderKey;

      default:
        return trashBinHeaderKey;
    }
  };

  /**
   * 文字列が存在するかを確認する関数
   * @param res - 判定する文字列(orUndefinedが入る)
   */
  private _distinctionNoneData = (res: string | undefined): string => {
    return res ? res : '--';
  };

  /**
   * Date型をstringに変更する関数
   * @param data - 変換の対象
   */
  private _convertDateToString = (data: Date | undefined) => {
    if (!!!data) return '--';
    return `${data.getFullYear()}/${data.getMonth() + 1}/${data.getDate()}`;
  };
}
